// react-query keys for the playlists area, in one place so a mutation can't invalidate a key that doesn't
// match what the list actually queries. A stale list after a save is the most common regression in this
// repo — every mutation below names the key it must invalidate.
export const mediaNetworkKeys = {
    /** mediaNetworkApi.list(brandId) — invalidate after create / update / delete of a playlist. */
    list: (brandId?: string) => ['playlists', brandId] as const,

    /** mediaNetworkApi.get(id) — invalidate after any item mutation (upload commit, delete, reorder), since
     *  items are returned as part of the playlist rather than as their own query. */
    detail: (playlistId?: string) => ['playlist', playlistId] as const,

    /** mediaNetworkApi.listSchedules(playlistId) — invalidate after schedule create / update / delete. */
    schedules: (playlistId?: string) => ['playlist-schedules', playlistId] as const,

    /** mediaNetworkApi.listConfigurations(brandId) — invalidate after configuration save / delete. */
    configurations: (brandId?: string) => ['playlist-configurations', brandId] as const,

    /** mediaNetworkApi.getEffectiveConfiguration(locationId) — what a location actually resolves to. */
    effectiveConfiguration: (locationId?: string) => ['playlist-configuration-effective', locationId] as const,

    /** Every location's effective configuration. Saving at ANY scope can change what a location below it
     *  resolves to, and there is no way to know which — so invalidate the lot, by prefix. Passing an
     *  undefined id to the key above would NOT match, since react-query compares the parts element-wise. */
    effectiveConfigurations: () => ['playlist-configuration-effective'] as const,

    /** mediaNetworkApi.listAdvertisers(brandId) — invalidate after advertiser create / update / delete. */
    advertisers: (brandId?: string) => ['playlist-advertisers', brandId] as const,

    /** mediaNetworkApi.listPlatformAdvertisers() - the TouchTab roster. Invalidate after any admin
     *  advertiser mutation. Separate from `advertisers`, which is one brand's filtered view of it. */
    platformAdvertisers: () => ['platform-advertisers'] as const,

    /** mediaNetworkApi.listAdvertiserGrants(advertiserId) - invalidate after grant / revoke, AND after
     *  toggling the brand's veto, since that changes a row this list shows. */
    advertiserGrants: (advertiserId?: string) => ['advertiser-grants', advertiserId] as const,

    /** mediaNetworkApi.listTags(brandId) — invalidate after tag create / rename / delete, AND after setting an
     *  item's attribution, because a tag's mediaCount changes with it. */
    tags: (brandId?: string) => ['playlist-tags', brandId] as const,

    /** mediaNetworkApi.listCreatives(brandId) — invalidate after any creative or rendition mutation,
     *  including attribution, since a creative carries its own tags and advertiser. */
    creatives: (brandId?: string) => ["media-creatives", brandId] as const,

    /** mediaNetworkApi.getCreative(id) — invalidate after a rendition mutation on that creative. */
    creative: (creativeId?: string) => ["media-creative", creativeId] as const,

    /** mediaNetworkApi.listDisplayProfiles() — platform reference data, so it effectively never changes. */
    displayProfiles: () => ["display-profiles"] as const,

    /** mediaNetworkApi.listBrandDisplayProfiles(brandId) — invalidate after setBrandDisplayProfiles. */
    brandDisplayProfiles: (brandId?: string) => ["brand-display-profiles", brandId] as const,

    // Day part keys live in @/api/account-management/dayPartApi (dayPartKeys) — they aren't playlist-owned.
};
